import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { platformApi } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { PageLoading } from '@/components/ui/loading';
import { formatDate } from '@/lib/utils';

interface AuditRow {
  id: string;
  user_id: string;
  organization_id: string | null;
  action: string;
  entity_type: string | null;
  entity_id: string | null;
  details: string | null;
  created_at: string;
}

const PAGE_SIZE = 25;

export default function PlatformAuditLogsPage() {
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ['platform', 'audit-logs', page],
    queryFn: async () => {
      const r = await platformApi.get<{ success: boolean; data: AuditRow[]; total: number }>(`/platform/audit-logs?page=${page}&limit=${PAGE_SIZE}`);
      return r;
    },
  });

  if (isLoading) return <PageLoading />;

  const totalPages = Math.max(1, Math.ceil((data?.total || 0) / PAGE_SIZE));

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-heading font-bold text-primary">Platform Audit Logs</h1>
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow><TableHead>When</TableHead><TableHead>Action</TableHead><TableHead>User</TableHead><TableHead>Org</TableHead><TableHead>Details</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {(data?.data ?? []).map((log) => (
                <TableRow key={log.id}>
                  <TableCell className="text-sm text-muted whitespace-nowrap">{formatDate(log.created_at)}</TableCell>
                  <TableCell><Badge variant="info">{log.action}</Badge></TableCell>
                  <TableCell className="text-sm font-mono">{log.user_id?.slice(0, 8) || '—'}</TableCell>
                  <TableCell className="text-sm font-mono">{log.organization_id?.slice(0, 8) || 'platform'}</TableCell>
                  <TableCell className="text-sm text-secondary max-w-md truncate">{log.details || '—'}</TableCell>
                </TableRow>
              ))}
              {(data?.data ?? []).length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted">No audit logs found</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      <div className="flex items-center justify-between">
        <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
        <span className="text-sm text-secondary">Page {page} of {totalPages}</span>
        <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
      </div>
    </div>
  );
}
